import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Save, Ambulance, CheckCircle } from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import ToastMsg from '../../components/common/ToastMsg';

const GENDERS = [
  { value: '', label: '— Select —' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

const EMPTY = {
  patient_name: '',
  patient_mobile: '',
  patient_age: '',
  patient_gender: '',
  pickup_location: '',
  condition: '',
  is_emergency: true,
};


export default function PartnerPortalNewPickup() {
  const navigate = useNavigate();
  const { create: apiCreate } = useApiMethods();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_name.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      const payload = {
        ...form,
        patient_age: form.patient_age === '' ? null : Number(form.patient_age),
      };
      const resp = await apiCreate('/engage/portal/ambulance/referrals/', payload);
      const data = resp?.data || resp?.data?.data;
      if (resp?.status === 'success' && data?.id) {
        setSubmitted(data);
        ToastMsg('success', 'Pickup logged.');
      } else {
        setError(resp?.message || 'Failed to log pickup.');
      }
    } catch (err) {
      const msg = err?.response?.data?.message
        || (err?.response?.data && JSON.stringify(err.response.data))
        || 'Failed to log pickup.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const submitAnother = () => {
    setForm(EMPTY);
    setSubmitted(null);
    setError(null);
  };

  const INPUT = "w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500";
  const L = ({ children }) => <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{children}</label>;

  if (submitted) {
    return (
      <div className="p-6 max-w-xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 flex items-center justify-center mx-auto mb-3">
            <CheckCircle className="w-8 h-8" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Pickup logged</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            The hospital will see <b>{submitted.patient_name}</b> as an incoming patient. Status will update as they arrive and get admitted.
          </p>
          <div className="flex items-center justify-center gap-2 mt-5">
            <button onClick={submitAnother} className="px-3 py-1.5 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
              Log another pickup
            </button>
            <Link to="/partner-portal/pickups" className="px-3 py-1.5 text-sm rounded-lg bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-sm hover:from-orange-600 hover:to-red-700">
              View my pickups
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 max-w-2xl mx-auto space-y-4">
      <button onClick={() => navigate('/partner-portal/pickups')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center text-white shadow-sm">
          <Ambulance className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Record a pickup</h1>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Quick entry — patient details help the hospital prepare in advance.</p>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <form onSubmit={submit} className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <L>Patient name <span className="text-red-500">*</span></L>
            <input required value={form.patient_name} onChange={e => set('patient_name', e.target.value)} className={INPUT} placeholder="e.g. Ravi Kumar" />
          </div>
          <div>
            <L>Mobile</L>
            <input value={form.patient_mobile} onChange={e => set('patient_mobile', e.target.value)} className={INPUT} placeholder="+91… (optional)" />
          </div>
          <div>
            <L>Age</L>
            <input type="number" min={0} max={140} value={form.patient_age} onChange={e => set('patient_age', e.target.value)} className={INPUT} placeholder="Optional" />
          </div>
          <div>
            <L>Gender</L>
            <select value={form.patient_gender} onChange={e => set('patient_gender', e.target.value)} className={INPUT}>
              {GENDERS.map(g => <option key={g.value || 'x'} value={g.value}>{g.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <L>Pickup location</L>
            <input value={form.pickup_location} onChange={e => set('pickup_location', e.target.value)} className={INPUT} placeholder="Address / landmark of pickup" />
          </div>
          <div className="md:col-span-2">
            <L>Condition / presenting complaint</L>
            <textarea rows={3} value={form.condition} onChange={e => set('condition', e.target.value)} className={INPUT}
              placeholder="e.g. Chest pain, road accident, fever with breathlessness…" />
          </div>
          <div className="md:col-span-2 flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id="pp-emergency"
              checked={!!form.is_emergency}
              onChange={e => set('is_emergency', e.target.checked)}
              className="w-4 h-4 accent-red-500"
            />
            <label htmlFor="pp-emergency" className="text-sm text-gray-700 dark:text-gray-300">
              Mark as emergency (alerts the ER team immediately)
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
          <button type="button" onClick={() => navigate('/partner-portal/pickups')}
            className="px-4 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            Cancel
          </button>
          <button type="submit" disabled={saving || !form.patient_name.trim()}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-red-600 text-white text-sm shadow-sm hover:from-orange-600 hover:to-red-700 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Logging…' : 'Log pickup'}
          </button>
        </div>
      </form>
    </div>
  );
}
